import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment, LinearProgress, Radio, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Tooltip, Typography } from "@mui/material";
import { useCallback, useRef, useState } from "react";
import useDebounce from "../../../lib/useDebounce";
import { useQuery } from "react-query";
import axiosClient from "../../../lib/axios";
import { IDataListaTitulosReceber } from "../../../types/api/financeiro/pesquisar-titulos-receber";
import LoadingApiQuery from "../../LoadingApiQuery";
import SearchIcon from "@mui/icons-material/Search";
import { formatarValorMonetario } from "../../../helpers/masks";
import moment from "moment";
import SelectPublico from "../SelectPublico";
import { IDataPublicos } from "../../../types/api/geral/pesquisar-publicos";
import { enqueueSnackbar } from "notistack";

export default function DialogSelectTitulos({
    open,
    extraParams = {},
    multiple = false,
    defaultValue = null,
    handleClose,
    onSubmit,
}: DialogSelectTitulosProps) {

    const [publico, setPublico] = useState<IDataPublicos | null>(null);
    const [keyword, setKeyword] = useState<string>("");
    const [selecionados, setSelecionados] = useState<IDataListaTitulosReceber[]>(() => {
        if (multiple) {
            return Array.isArray(defaultValue)
                ? defaultValue as IDataListaTitulosReceber[]
                : defaultValue
                    ? [defaultValue as IDataListaTitulosReceber]
                    : [];
        } else {
            return defaultValue
                ? [defaultValue as IDataListaTitulosReceber]
                : [];
        }
    });
    const [limit, setLimit] = useState<number>(50);
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const [sortConfig, setSortConfig] = useState<{ column: 'data_vencimento'; direction: 'asc' | 'desc' } | null>(null);

    // Queries
    const debouncedKeyword = useDebounce(keyword);
    const { data, isFetching, isLoading } = useQuery({
        queryKey: [
            "/financeiro/contas-receber/pesquisar-titulos-receber",
            debouncedKeyword,
            limit,
            extraParams,
            publico,
            sortConfig
        ],
        queryFn: () =>
            axiosClient.get("/financeiro/contas-receber/pesquisar-titulos-receber", {
                params: {
                    page: 1,
                    limit: limit,
                    pesquisar: debouncedKeyword,
                    tipo_publico_contem: [publico?.tipo_publico],
                    codigo_publico_contem: [publico?.codigo_publico],
                    ...extraParams,
                    ...(sortConfig && {
                        ordering: JSON.stringify([{
                            column: sortConfig.column,
                            direction: sortConfig.direction.toUpperCase()
                        }])
                    })
                },
            }),
        retry: false,
        keepPreviousData: true,
        cacheTime: 0,
    });
    const dadosTitulos: IDataListaTitulosReceber[] = data?.data.data || [];

    //Funções manipuladoras
    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const bottom =
            event.currentTarget.scrollHeight - event.currentTarget.scrollTop ===
            event.currentTarget.clientHeight;
        if (bottom && !isFetching) {
            setLimit((prevLimit) => prevLimit + 20);
        }
    };

    const handleClick = useCallback((item: IDataListaTitulosReceber) => {
        if (multiple) {

            const isSamePublico = selecionados.some(selected => `${selected.tipo_publico}${selected.codigo_publico}` === `${item.tipo_publico}${item.codigo_publico}`);

            if (!isSamePublico && selecionados.length > 0) {
                return enqueueSnackbar('Não é possível selecionar títulos de públicos diferentes.', { variant: 'error' });
            }
            setSelecionados(prev => {
                const index = prev.findIndex(i => i.id === item.id);
                return index === -1
                    ? [...prev, item]
                    : prev.filter((_, i) => i !== index);
            });
        } else {
            setSelecionados([item]);
        }
    }, [multiple, selecionados]);

    const handleSubmit = useCallback(() => {
        if (!multiple) {
            onSubmit(selecionados[0] || null);
        } else {
            onSubmit(selecionados);
        }
        handleClose();
    }, [onSubmit, selecionados, multiple, handleClose]);

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const titulosSelecionados = [
                ...selecionados,
                ...dadosTitulos.filter(item =>
                    !selecionados.some(selected => selected.id === item.id)
                ),
            ];

            const publicosUnicos = Array.from(
                new Set(
                    titulosSelecionados.map(
                        item => `${item.tipo_publico}${item.codigo_publico}`
                    )
                )
            );

            if (publicosUnicos.length > 1) {
                enqueueSnackbar('Não é possível selecionar títulos de públicos diferentes.', { variant: 'error' });
                return;
            }

            setSelecionados(titulosSelecionados);
        } else {

            setSelecionados(prev =>
                prev.filter(selected =>
                    !dadosTitulos.some(item => selected.id === item.id)
                )
            );
        }
    };

    const handleSort = (field: 'data_vencimento') => {
        setSortConfig(prevSort => {
            if (prevSort?.column === field) {
                return {
                    column: field,
                    direction: prevSort.direction === 'asc' ? 'desc' : 'asc'
                };
            }
            return { column: field, direction: 'asc' };
        });
    };
    //

    // Valores importantes
    const todosItensSelecionados = dadosTitulos.length > 0 &&
        dadosTitulos.every(item =>
            selecionados.some(selected => selected.id === item.id)
        );

    const algunsItensSelecionados = dadosTitulos.some(item =>
        selecionados.some(selected => selected.id === item.id)
    );

    const bloquearConfirmar = selecionados.length === 0;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            scroll="body"
            fullWidth
            maxWidth="lg"
        >
            <DialogTitle>Selecionar títulos a receber</DialogTitle>

            <DialogContent>
                <Box py={2}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                value={keyword}
                                label="Pesquisar"
                                onChange={({ target }) => setKeyword(target.value)}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <SearchIcon />
                                            </InputAdornment>
                                        )
                                    }
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <SelectPublico
                                value={publico}
                                onChange={(data) => setPublico(data)}
                            />
                        </Grid>
                    </Grid>


                    {isLoading && (
                        <LoadingApiQuery
                            extraParams={{ height: 400, my: 1, alignItems: "center" }}
                        />
                    )}

                    <Box mt={1}>{isFetching && data && <LinearProgress />}</Box>

                    {!isLoading && data && (
                        <TableContainer
                            ref={tableContainerRef}
                            onScroll={handleScroll}
                            sx={{ height: 400, overflow: "auto", mb: 1 }}
                        >
                            <Table size="small" aria-label="a dense table" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            {multiple && dadosTitulos.length > 0 && (
                                                <Tooltip title="Selecionar todos os itens visíveis">
                                                    <Checkbox
                                                        checked={todosItensSelecionados}
                                                        indeterminate={algunsItensSelecionados && !todosItensSelecionados}
                                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                                    />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell><b>Título</b></TableCell>
                                        <TableCell><b>Emissão</b></TableCell>

                                        <TableCell>
                                            <TableSortLabel
                                                active={sortConfig?.column === 'data_vencimento'}
                                                direction={sortConfig?.column === 'data_vencimento' ? sortConfig.direction : 'asc'}
                                                onClick={() => handleSort('data_vencimento')}
                                            >
                                                <b>Vencimento</b>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell><b>Valor a Receber</b></TableCell>
                                        <TableCell><b>Saldo Principal</b></TableCell>
                                        <TableCell><b>Público</b></TableCell>
                                        <TableCell><b>Histórico</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dadosTitulos.map((row) => {
                                        const isChecked = selecionados.some(item => item.id === row.id);

                                        return (
                                            <TableRow
                                                key={row.id}
                                                sx={{
                                                    cursor: "pointer",
                                                    backgroundColor: isChecked ? "#e3f2fd" : "unset",
                                                    "&:hover": { backgroundColor: "#f5f5f5" },
                                                    "&:last-child td, &:last-child th": { border: 0 },
                                                }}
                                                onClick={() => handleClick(row)}
                                            >
                                                <TableCell>
                                                    {multiple ? (
                                                        <Checkbox checked={isChecked} />
                                                    ) : (
                                                        <Radio checked={isChecked} />
                                                    )}
                                                </TableCell>
                                                <TableCell>{row.id}</TableCell>
                                                <TableCell>{moment(row.data_emissao).format("DD/MM/YYYY")}</TableCell>
                                                <TableCell>
                                                    {moment(row.data_vencimento).format("DD/MM/YYYY")}
                                                </TableCell>
                                                <TableCell>
                                                    {formatarValorMonetario(row.valor_receber)}
                                                </TableCell>
                                                <TableCell>
                                                    {formatarValorMonetario(row.saldo_principal)}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {row.tipo_publico}{row.codigo_publico} - {row.nome_publico}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            maxWidth: 200,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {row.historico}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>Cancelar</Button>
                <Button
                    autoFocus
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={bloquearConfirmar}
                >
                    Selecionar {multiple && selecionados.length > 0 ? `(${selecionados.length})` : ""}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

interface DialogSelectTitulosProps {
    open: boolean;
    handleClose: () => void;
    onSubmit: (value: IDataListaTitulosReceber | IDataListaTitulosReceber[] | null) => void;
    extraParams?: { [key: string]: any };
    multiple?: boolean;
    defaultValue?: IDataListaTitulosReceber | IDataListaTitulosReceber[] | null;
}
